import {
   AgentSkill,
   AgentSkillListResponse,
   SkillFileUrlResponse,
   SkillFilesResponse,
} from "@/types/skills";
import { apiFetchRaw, apiFetch } from "@/lib/api";
import {GAIL_API} from "@/utils/common/constants/routes";
import {LocalizedSkillError, normalizeSkillDefinitionFiles, sanitizeSkillMdFrontmatter} from "@/utils/skills";

/**
 * Parse a JSON:API-style error response and return the first error's detail message.
 * Falls back to the raw status text if the body isn't parseable.
 */
async function readApiError(res: Response, fallback: string): Promise<string> {
   try {
      const body = await res.json() as {errors?: {detail?: string; title?: string}[]};
      const first = body?.errors?.[0];
      if (first?.detail) return first.detail;
      if (first?.title) return first.title;
   } catch {
      // body wasn't JSON — fall through
   }
   return res.statusText || fallback;
}

/**
 * Defense-in-depth, run by every create/update path:
 *  1. normalize — rename the resolved skill definition file to the canonical
 *     SKILL.md (the definition may be any root-level Markdown file, e.g.
 *     `my-skill.md`), so the backend contract stays fixed.
 *  2. sanitize — quote YAML frontmatter values in SKILL.md that contain
 *     special characters (colons, etc.). Non-SKILL.md files pass through.
 * The Galactus backend does NOT sanitize YAML, so this is the last line of
 * defense before bad frontmatter causes a parse error. If a new upload path
 * is added, it MUST call prepareFiles.
 */
const MAX_SKILL_MD_BYTES = 512 * 1024; // 512 KB — generous for a markdown file

async function sanitizeFiles(files: File[]): Promise<File[]> {
   return Promise.all(
      files.map(async (file) => {
         if (file.name.toLowerCase() !== "skill.md") return file;
         if (file.size > MAX_SKILL_MD_BYTES) return file; // skip sanitization on oversized files
         const content = await file.text();
         const sanitized = sanitizeSkillMdFrontmatter(content);
         if (sanitized === content) return file;
         return new File([sanitized], file.name, {type: file.type});
      })
   );
}

async function prepareFiles(files: File[]): Promise<File[]> {
   return sanitizeFiles(normalizeSkillDefinitionFiles(files));
}

/**
 * Client-side service for skill CRUD operations.
 *
 * All operations call the Galactus API directly — `apiFetch` / `apiFetchRaw`
 * handle auth via cookies (`credentials: "include"`).
 */
export class SkillsService {
   static async list(page = 1, size = 50) {
      return apiFetch<AgentSkillListResponse>(
         `${GAIL_API}/.internal/skills?page=${page}&size=${size}`,
         "GET",
         {alwaysReturn: true}
      );
   }

   /** Fetch all skills across all pages. */
   static async listAll(size = 50): Promise<AgentSkill[]> {
      const first = await SkillsService.list(1, size);
      if (!first || !Array.isArray(first.data)) {
         throw new Error("Failed to fetch skills");
      }

      const allSkills = [...first.data];
      const totalPages = first.pages ?? 1;

      if (totalPages > 1) {
         const remaining = await Promise.all(
            Array.from({length: totalPages - 1}, (_, i) => SkillsService.list(i + 2, size))
         );
         for (const page of remaining) {
            if (!page || !Array.isArray(page.data)) {
               throw new Error("Failed to fetch skills");
            }
            allSkills.push(...page.data);
         }
      }

      return allSkills;
   }

   static async get(id: string) {
      return apiFetch<AgentSkill>(
         `${GAIL_API}/.internal/skills/${id}`,
         "GET",
         {alwaysReturn: true}
      );
   }

   /**
    * Throws on failure with the server's error detail message, so callers can
    * surface a specific reason (e.g. "Skill name cannot be changed after creation")
    * rather than a generic "failed to upload" alert.
    */
   static async create(files: File[]): Promise<AgentSkill> {
      const safe = await prepareFiles(files);
      const formData = new FormData();
      for (const file of safe) {
         formData.append("files", file, file.name);
      }

      let res: Response;
      try {
         res = await apiFetchRaw(`${GAIL_API}/.internal/skills`, "POST", { body: formData });
      } catch {
         throw new LocalizedSkillError("upload.error", "Network error while creating skill");
      }

      if (!res.ok) throw new Error(await readApiError(res, "Failed to create skill"));
      return (await res.json()) as AgentSkill;
   }

   static async update(id: string, files: File[]): Promise<AgentSkill> {
      const safe = await prepareFiles(files);
      const formData = new FormData();
      for (const file of safe) {
         formData.append("files", file, file.name);
      }

      let res: Response;
      try {
         res = await apiFetchRaw(`${GAIL_API}/.internal/skills/${id}`, "PUT", { body: formData });
      } catch {
         throw new LocalizedSkillError("upload.updateError", "Network error while updating skill");
      }

      if (!res.ok) throw new Error(await readApiError(res, "Failed to update skill"));
      return (await res.json()) as AgentSkill;
   }

   static async remove(id: string) {
      return apiFetch<void>(
         `${GAIL_API}/.internal/skills/${id}`,
         "DELETE"
      );
   }

   static async listFiles(id: string) {
      return apiFetch<SkillFilesResponse>(
         `${GAIL_API}/.internal/skills/${id}/files`,
         "GET",
         {alwaysReturn: true}
      );
   }

   static async getFileUrl(id: string, path: string) {
      return apiFetch<SkillFileUrlResponse>(
         `${GAIL_API}/.internal/skills/${id}/files/${path}`,
         "GET",
         {alwaysReturn: true}
      );
   }
}
